// ============================================================
// useLiveEditNotice.ts — (D12 live updates) someone else changed what a card
// shows while THIS user has it open for editing.
//
// Never overwrite an unsaved form: while `editing`, a remote change to the
// card's data (`kinds` on the change bus) doesn't replace the draft — it
// raises a notice instead; components/shared/LiveUpdateNote shows "Updated
// by Ollie just now — Reload". Catch-up re-reads (reconnect, tab came back)
// are not "a change" and never raise it. Closing the card clears it.
// The user's Save still goes through the card's lost-update check
// (baseUpdatedAt / 409) — nothing here weakens it.
// With `holdCaseRefresh`, the open case's store re-read (hooks/
// liveCaseRefresh) also waits while the card is open — for cards that edit
// the case row through the store.
// ============================================================
import { useCallback, useEffect, useState } from 'react'
import { useCaseChange, type CaseChangeKind } from '../utils/caseChangeEvents'
import { useCasevoStore } from '../store/useCasevoStore'
import { useCaseRefreshHold } from './liveCaseRefresh'

export interface LiveEditNotice {
  // "Ollie" / "you (another tab)" / "someone".
  by: string
  at: number
}

export const actorLabel = (actorUserId: string | null | undefined): string => {
  if (!actorUserId) return 'someone'
  const st = useCasevoStore.getState()
  if (st.authUser?.id === actorUserId) return 'you (another tab)'
  const u = st.getFirmUserById?.(actorUserId)
  const first = u?.firstName?.trim()
  return first || (u ? `${u.firstName} ${u.lastName}`.trim() : '') || 'someone'
}

export const useLiveEditNotice = (
  caseId: string | null | undefined,
  kinds: readonly CaseChangeKind[],
  editing: boolean,
  opts: { holdCaseRefresh?: boolean } = {},
): { notice: LiveEditNotice | null; clear: () => void } => {
  const [notice, setNotice] = useState<LiveEditNotice | null>(null)
  useCaseChange(caseId, kinds, (_k, info) => {
    if (!editing || !info.remote || info.resync) return
    setNotice({ by: actorLabel(info.actorUserId), at: Date.now() })
  })
  useEffect(() => { if (!editing) setNotice(null) }, [editing])
  useCaseRefreshHold(caseId, !!opts.holdCaseRefresh && editing)
  const clear = useCallback(() => setNotice(null), [])
  return { notice, clear }
}

export default useLiveEditNotice
